import React from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListSubheader,
  Paper,
  Popper,
  Typography,
} from '@mui/material';

import type { ChatSkill } from '../chatSkills';

type SlashSkillsMenuProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  skills: ChatSkill[];
  highlightedIndex: number;
  onSelect: (skill: ChatSkill) => void;
  onHighlightChange: (index: number) => void;
};

/**
 * Skills menu above the input. No ClickAwayListener: the textarea is outside the Paper,
 * so clicks there would count as “away” and clear the field.
 * Close with Esc (handled in InputBlock) or by picking a skill.
 */
export const SlashSkillsMenu: React.FC<SlashSkillsMenuProps> = ({
  open,
  anchorEl,
  skills,
  highlightedIndex,
  onSelect,
  onHighlightChange,
}) => {
  if (!open || !anchorEl) return null;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top-start"
      modifiers={[
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'preventOverflow', options: { padding: 8 } },
      ]}
      style={{
        zIndex: 1300,
        width: anchorEl.getBoundingClientRect().width,
        maxWidth: 480,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          maxHeight: 360,
          overflow: 'auto',
          borderRadius: '6px',
          border: (theme) => `1px solid ${theme.palette.grey[100]}`,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? theme.palette.grey[900]
              : theme.palette.background.paper,
        }}
      >
        <List dense disablePadding>
          <ListSubheader
            component="div"
            sx={{
              bgcolor: 'transparent',
              lineHeight: 1.2,
              py: 0.75,
              px: 1.5,
              fontSize: 9,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'text.secondary',
            }}
          >
            Skills
          </ListSubheader>
          {skills.length === 0 ? (
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 10.5, lineHeight: 1.4, display: 'block' }}
              >
                No matching skills. Try different text after `/` or press Esc.
              </Typography>
            </Box>
          ) : (
            skills.map((skill, index) => (
              <ListItemButton
                key={skill.id}
                selected={index === highlightedIndex}
                onMouseEnter={() => onHighlightChange(index)}
                onClick={() => onSelect(skill)}
                sx={{
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  py: 0.5,
                  px: 1,
                  borderRadius: 1,
                  mx: 0.5,
                  mb: 0.125,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: 11,
                    lineHeight: 1.25,
                    fontWeight: 600,
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, monospace',
                  }}
                >
                  /{skill.command}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    mt: 0.125,
                    fontSize: 10.5,
                    lineHeight: 1.3,
                    fontWeight: 500,
                  }}
                >
                  {skill.label}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    mt: 0.125,
                    opacity: 0.88,
                    fontSize: 10,
                    lineHeight: 1.35,
                  }}
                >
                  {skill.description}
                </Typography>
              </ListItemButton>
            ))
          )}
        </List>
      </Paper>
    </Popper>
  );
};
